import { Navigate } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { useAssessment } from "@/context/AssessmentContext";
import { isProviderIntakeComplete } from "@/lib/questionnaire";

/*
  P4-SAFE-MIN — Intake-completion commercial-boundary screen.

  Terminal free step in the public journey. Confirms that the initial
  provider intake has been received and directs the provider to contact
  Spiral Beyond Compliance for the readiness-scoping conversation. There
  is no public path from here into /questionnaire, /results or /report —
  those routes are Owner/Admin-only via <RequireAdmin>.

  Deliberately does not:
    * display any price, GST, deposit or checkout;
    * promise a free consultation, snapshot, or report;
    * offer a "Continue to questionnaire" action;
    * assert certification, audit or registration authority.
*/
const CONTACT_MAILTO =
  "mailto:hello@spiralbeyondcompliance.com.au?subject=NDIS%200138%20Readiness%20conversation";

export default function IntakeCompletePage() {
  const { answers, providerIntakeCompletedAt } = useAssessment();

  // If the intake isn't actually complete, send them back to the intake stage.
  if (!isProviderIntakeComplete(answers) && !providerIntakeCompletedAt) {
    return <Navigate to="/intake" replace />;
  }

  return (
    <PageShell>
      <section className="container max-w-3xl py-14" data-testid="intake-complete-boundary">
        <p className="eyebrow">Intake received</p>
        <h1 className="mt-3 font-display text-4xl font-medium leading-tight text-foreground sm:text-5xl">
          Your initial NDIS 0138 intake is complete
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-foreground/90">
          Thank you. Spiral Beyond Compliance will review the information provided and confirm the appropriate next step. Detailed readiness assessment, evidence review and client reporting are provided through an agreed consultancy scope.
        </p>
        <div className="mt-8">
          <Button asChild className="rounded-sm px-5 text-[13px] font-medium tracking-wide shadow-paper">
            <a href={CONTACT_MAILTO} data-testid="intake-complete-contact-cta">
              Contact Spiral Beyond Compliance
            </a>
          </Button>
        </div>
        <p
          className="mt-10 border-t border-border pt-6 text-xs leading-relaxed text-muted-foreground"
          data-testid="intake-complete-boundary-statement"
        >
          This intake does not constitute an audit, certification, registration decision or guarantee of compliance.
        </p>
      </section>
    </PageShell>
  );
}
